import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

function Drawer({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="drawer" {...props} />;
}

function DrawerTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

function DrawerPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

function DrawerClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="drawer-close" {...props} />;
}

function DrawerOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="drawer-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/50 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className
      )}
      {...props}
    />
  );
}

function DrawerContent({
  className,
  children,
  onOpenAutoFocus,
  onSwipeDown,
  description = 'Drawer content',
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  description?: string;
  onSwipeDown?: () => void;
  showCloseButton?: boolean;
}) {
  const pointerStartYRef = React.useRef<number | null>(null);
  const pointerStartXRef = React.useRef<number | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerStartYRef.current = event.clientY;
    pointerStartXRef.current = event.clientX;
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStartYRef.current === null || pointerStartXRef.current === null) {
      return;
    }

    const deltaY = event.clientY - pointerStartYRef.current;
    const deltaX = Math.abs(event.clientX - pointerStartXRef.current);

    pointerStartYRef.current = null;
    pointerStartXRef.current = null;

    if (deltaY > 60 && deltaY > deltaX) {
      onSwipeDown?.();
    }
  };

  const handleOpenAutoFocus: React.ComponentProps<typeof DialogPrimitive.Content>['onOpenAutoFocus'] = (event) => {
    event.preventDefault();
    contentRef.current?.focus();
    onOpenAutoFocus?.(event);
  };

  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DialogPrimitive.Content
        ref={contentRef}
        data-slot="drawer-content"
        tabIndex={-1}
        className={cn(
          'fixed inset-x-0 bottom-0 z-50 box-border flex max-h-[90vh] w-full max-w-[100dvw] flex-col overflow-x-hidden rounded-t-3xl border bg-background shadow-lg outline-none data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom',
          className
        )}
        aria-describedby={props['aria-describedby'] ?? undefined}
        onOpenAutoFocus={handleOpenAutoFocus}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        {...props}
      >
        <DialogPrimitive.Description className="sr-only">
          {description}
        </DialogPrimitive.Description>
        <div className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-muted" data-testid="drawer-handle" />
        {children}
        {showCloseButton ? (
          <DialogPrimitive.Close
            data-slot="drawer-close"
            className="absolute right-4 top-4 rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <XIcon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        ) : null}
      </DialogPrimitive.Content>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="drawer-header" className={cn('flex min-w-0 max-w-full flex-col gap-1 px-4 pb-3 pt-2', className)} {...props} />;
}

function DrawerBody({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="drawer-body" className={cn('min-h-0 w-full min-w-0 max-w-full flex-1 overflow-x-hidden overflow-y-auto p-4 pt-3', className)} {...props} />;
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="drawer-footer" className={cn('mt-auto flex flex-col gap-2 p-6 pt-0', className)} {...props} />;
}

function DrawerTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title data-slot="drawer-title" className={cn('text-lg font-semibold', className)} {...props} />;
}

function DrawerDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description data-slot="drawer-description" className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

export {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
};
